// The Terminal command line's own keyboard bindings (#4398, epic #4391) —
// mirrors `lib/hotkeys.ts`'s shape exactly: pure chord predicates, a
// co-located `SHORTCUTS` table, and ONE document `keydown` listener.
// `isTypingTarget` is imported from `../hotkeys`, never rewritten — the same
// guard that keeps the app's global shortcuts out of a pane's filter input
// keeps this module's own focus binding out of it too.
//
// TWO KINDS OF BINDING, ONE LISTENER. `focus-repl` is a GLOBAL binding: it
// must fire from anywhere in the app EXCEPT while something is already being
// typed into (a pane's filter box, any other field) — the same shape as
// `isShortcutsChord`'s `?`. The other four (history-prev/next, submit, blur)
// only make sense once the command line's OWN input already has focus, so
// they are guarded by `event.target === inputRef()` rather than
// `isTypingTarget` — a guard general enough to admit the pane filter input
// would fire "submit" while someone is typing `actor=` into it.
//
// `:` (vim's own command-mode key), unmodified, outside a typing target — free
// today: `hotkeys.ts`'s own `SHORTCUTS` binds `⌘K`/`Ctrl K`, `?` and `[` (the
// rail's collapse, #7710), none of them `:`.
//
// PANE CHORDS — Terminator's own defaults, GLOBAL like `:` (outside a typing
// target). Ctrl+Shift+E is Terminator's "split vertically": the new pane
// appears BESIDE, which is `layout.ts`'s `row` — the OPPOSITE of this
// parser's `split -v` (below). The axis is bound by effect, never by the word
// "vertical". Ctrl+Shift+X is zoom. Terminator's Ctrl+Shift+O (split below)
// and Ctrl+Shift+W (close) are the browser's own new/close-tab keys and never
// reach the page, so those two are bound on Alt+Shift only, and E and X take
// the Alt+Shift fallback too (Firefox claims both Ctrl+Shift forms for its
// devtools and text direction). Next/previous pane is Alt+Shift+N/P, NOT
// Alt+Shift+Tab: that is the OS window switcher on Windows and GNOME, caught
// before the page sees it — a binding that silently never fires. Chords match
// `event.code`, not `event.key`: macOS turns Alt+Shift+letter into a symbol.

import { onCleanup, onMount } from 'solid-js';
import type { ShortcutDoc } from '../hotkeys';
import { isTypingTarget } from '../hotkeys';

/** Does this event ask to focus the command line? Unmodified `:`, outside a text field. */
export function isFocusReplChord(event: KeyboardEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  return event.key === ':' && !isTypingTarget(event.target);
}

const isCtrlShift = (e: KeyboardEvent): boolean =>
  e.ctrlKey && e.shiftKey && !e.altKey && !e.metaKey;
const isAltShift = (e: KeyboardEvent): boolean =>
  e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey;

/** A global pane chord: `code` under Alt+Shift (or also Ctrl+Shift), never inside a text field. */
function paneChord(event: KeyboardEvent, code: string, ctrlShiftToo: boolean): boolean {
  if (event.code !== code || isTypingTarget(event.target)) return false;
  return isAltShift(event) || (ctrlShiftToo && isCtrlShift(event));
}

/** Split the focused pane, new pane BESIDE (`row`) — Terminator's Ctrl+Shift+E. */
export function isSplitRowChord(event: KeyboardEvent): boolean {
  return paneChord(event, 'KeyE', true);
}

/** Split the focused pane, new pane BELOW (`column`) — Alt+Shift+O only. */
export function isSplitColumnChord(event: KeyboardEvent): boolean {
  return paneChord(event, 'KeyO', false);
}

/** Zoom / restore the focused pane — Terminator's Ctrl+Shift+X. */
export function isZoomChord(event: KeyboardEvent): boolean {
  return paneChord(event, 'KeyX', true);
}

/** Close the focused pane — Alt+Shift+W only. */
export function isClosePaneChord(event: KeyboardEvent): boolean {
  return paneChord(event, 'KeyW', false);
}

/** Move focus to the next pane — Alt+Shift+N. */
export function isNextPaneChord(event: KeyboardEvent): boolean {
  return paneChord(event, 'KeyN', false);
}

/** Move focus to the previous pane — Alt+Shift+P. */
export function isPrevPaneChord(event: KeyboardEvent): boolean {
  return paneChord(event, 'KeyP', false);
}

/**
 * Search the focused pane — Ctrl+F (⌘F on macOS). Fires from ANYWHERE on the
 * Terminal page, text fields included: this listener only exists while the
 * console is mounted, so taking the browser's find here is scoped to the one
 * screen where a pane search replaces it (#4401).
 */
export function isSearchChord(event: KeyboardEvent): boolean {
  if (event.altKey || event.shiftKey) return false;
  return (event.ctrlKey || event.metaKey) && event.code === 'KeyF';
}

/** Complete the word at the end of the command line — plain Tab. */
export function isCompleteChord(event: KeyboardEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return false;
  return event.key === 'Tab';
}

/** Step to the previous (older) line in command history. */
export function isHistoryPrevChord(event: KeyboardEvent): boolean {
  return event.key === 'ArrowUp';
}

/** Step to the next (newer) line in command history. */
export function isHistoryNextChord(event: KeyboardEvent): boolean {
  return event.key === 'ArrowDown';
}

/** Run the command line's current input. */
export function isSubmitChord(event: KeyboardEvent): boolean {
  return event.key === 'Enter';
}

/** Leave the command line without running anything. */
export function isBlurChord(event: KeyboardEvent): boolean {
  return event.key === 'Escape';
}

export type TerminalKeymapHandlers = {
  readonly onFocusRepl: () => void;
  readonly onHistoryPrev: () => void;
  readonly onHistoryNext: () => void;
  readonly onSubmit: () => void;
  readonly onBlur: () => void;
  /** Returns whether it completed anything — Tab is only swallowed when it did. */
  readonly onComplete: () => boolean;
  readonly onSplitRow: () => void;
  readonly onSplitColumn: () => void;
  readonly onToggleZoom: () => void;
  readonly onClosePane: () => void;
  readonly onCyclePane: (direction: 1 | -1) => void;
  /** Ctrl/⌘+F — open (or re-focus) the focused pane's search. */
  readonly onSearch: () => void;
};

/**
 * What `help` (and the `?` sheet) lists for this module — see `hotkeys.ts`'s
 * own `SHORTCUTS`. Every pane chord's menu equivalent already exists: the
 * pane title bar's split/zoom/close buttons, and clicking a pane to focus it.
 */
export const SHORTCUTS: readonly ShortcutDoc[] = [
  { combos: [':'], label: 'terminal.keymap_focus' },
  { combos: ['↑'], label: 'terminal.keymap_history_prev' },
  { combos: ['↓'], label: 'terminal.keymap_history_next' },
  { combos: ['Enter'], label: 'terminal.keymap_submit' },
  { combos: ['Esc'], label: 'terminal.keymap_blur' },
  { combos: ['Tab'], label: 'terminal.keymap_complete' },
  { combos: ['Ctrl Shift E', 'Alt Shift E'], label: 'terminal.keymap_split_row' },
  { combos: ['Alt Shift O'], label: 'terminal.keymap_split_column' },
  { combos: ['Ctrl Shift X', 'Alt Shift X'], label: 'terminal.keymap_zoom' },
  { combos: ['Alt Shift W'], label: 'terminal.keymap_close' },
  { combos: ['Alt Shift N'], label: 'terminal.keymap_next_pane' },
  { combos: ['Alt Shift P'], label: 'terminal.keymap_prev_pane' },
  { combos: ['Ctrl F', '⌘ F'], label: 'terminal.keymap_search' },
];

/** The global pane chords, in match order — one table so the listener has no ladder to drift. */
function paneAction(event: KeyboardEvent, h: TerminalKeymapHandlers): (() => void) | null {
  if (isSplitRowChord(event)) return h.onSplitRow;
  if (isSplitColumnChord(event)) return h.onSplitColumn;
  if (isZoomChord(event)) return h.onToggleZoom;
  if (isClosePaneChord(event)) return h.onClosePane;
  if (isNextPaneChord(event)) return () => h.onCyclePane(1);
  if (isPrevPaneChord(event)) return () => h.onCyclePane(-1);
  return null;
}

/** Every chord that fires from outside the command line's input: `:`, Ctrl/⌘+F, the pane chords. */
function globalAction(event: KeyboardEvent, h: TerminalKeymapHandlers): (() => void) | null {
  if (isFocusReplChord(event)) return h.onFocusRepl;
  if (isSearchChord(event)) return h.onSearch;
  return paneAction(event, h);
}

/** The chords that only apply inside the command line's own input. */
function inputAction(event: KeyboardEvent, h: TerminalKeymapHandlers): (() => void) | null {
  if (isHistoryPrevChord(event)) return h.onHistoryPrev;
  if (isHistoryNextChord(event)) return h.onHistoryNext;
  if (isSubmitChord(event)) return h.onSubmit;
  if (isBlurChord(event)) return h.onBlur;
  return null;
}

/**
 * Bind the command line's keys for the lifetime of the calling component —
 * one document `keydown` listener, the same shape as `createHotkeys`.
 * `inputRef` is read fresh on every event (not tracked), so it is safe to
 * pass a closure over a plain (non-signal) ref variable.
 */
export function createTerminalKeymap(
  handlers: TerminalKeymapHandlers,
  inputRef: () => HTMLInputElement | undefined,
): void {
  onMount(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.repeat || event.defaultPrevented) return;
      const global = globalAction(event, handlers);
      if (global !== null) {
        event.preventDefault();
        global();
        return;
      }
      // Everything else only makes sense while the command line's OWN input
      // has focus — never while typing in a pane's filter box or anywhere else.
      if (event.target !== inputRef()) return;
      // Tab is swallowed ONLY when it completed something: an empty word or
      // no match lets it move focus on, so completion is never a focus trap.
      if (isCompleteChord(event)) {
        if (handlers.onComplete()) event.preventDefault();
        return;
      }
      const action = inputAction(event, handlers);
      if (action !== null) {
        event.preventDefault();
        action();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    onCleanup(() => document.removeEventListener('keydown', onKeyDown));
  });
}
